import type {
  InputMethod,
  GenerationSessionResponse,
  GenerationVariantResponse,
  WorkshopStep,
} from '../types/workshop.types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * URL step parameter values
 * Note: 'generating' uses the base URL without step param
 * 'style-preview' is now supported as explicit step for backtracking
 */
type UrlStepParam = 'theme' | 'product' | 'style-preview';

/**
 * Valid input method URL parameter values
 */
const VALID_INPUT_METHODS: InputMethod[] = ['photo', 'prompt', 'selections'];

/**
 * Parameters that can be encoded in the workshop URL
 */
interface WorkshopUrlParams {
  /** Generation session ID */
  sessionId?: string | undefined;
  /** Input method (photo, prompt, selections) */
  method?: InputMethod | undefined;
  /** Explicit step override (theme, product, or style-preview) */
  step?: UrlStepParam | undefined;
  /** Selected style variant ID (used when step=theme) */
  styleVariantId?: string | undefined;
  /** Created design asset ID (used when step=product) */
  designAssetId?: string | undefined;
}

/**
 * Parsed URL state with determined step
 */
export interface ParsedWorkshopUrl {
  /** Generation session ID from URL path */
  sessionId: string | null;
  /** Input method from query param */
  method: InputMethod | null;
  /** Explicit step from query param */
  explicitStep: UrlStepParam | null;
  /** Style variant ID from query param */
  styleVariantId: string | null;
  /** Design asset ID from query param */
  designAssetId: string | null;
}

// ============================================================================
// URL BUILDING
// ============================================================================

/**
 * Build a workshop URL from parameters
 *
 * @example
 * buildWorkshopUrl({}) → '/workshop'
 * buildWorkshopUrl({ method: 'photo' }) → '/workshop?method=photo'
 * buildWorkshopUrl({ sessionId: 'abc', method: 'photo' }) → '/workshop/abc?method=photo'
 * buildWorkshopUrl({ sessionId: 'abc', method: 'photo', step: 'theme', styleVariantId: 'xyz' }) → '/workshop/abc?method=photo&step=theme&style=xyz'
 */
export function buildWorkshopUrl(params: WorkshopUrlParams): string {
  const { sessionId, method, step, styleVariantId, designAssetId } = params;

  // Base path
  let url = '/workshop';

  // Add session ID to path
  if (sessionId) {
    url += `/${sessionId}`;
  }

  // Build query params
  const queryParams = new URLSearchParams();

  // Method param comes first for readability
  if (method) {
    queryParams.set('method', method);
  }

  if (step) {
    queryParams.set('step', step);
  }

  if (styleVariantId) {
    queryParams.set('style', styleVariantId);
  }

  if (designAssetId) {
    queryParams.set('design', designAssetId);
  }

  // Append query string if we have params
  const queryString = queryParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}

/**
 * Build URL for input method selection (before session)
 *
 * @example
 * buildMethodUrl('photo') → '/workshop?method=photo'
 * buildMethodUrl('selections') → '/workshop?method=selections'
 */
export function buildMethodUrl(method: InputMethod): string {
  return buildWorkshopUrl({ method });
}

/**
 * Build URL for style preview step (alias for generating step)
 * Both steps use the same base URL structure
 */
export function buildStylePreviewUrl(sessionId: string, method?: InputMethod): string {
  return buildWorkshopUrl({ sessionId, method, step: 'style-preview' });
}

/**
 * Build URL for theme results step (after style selection)
 */
export function buildThemeResultsUrl(
  sessionId: string,
  styleVariantId: string,
  method?: InputMethod
): string {
  return buildWorkshopUrl({
    sessionId,
    method,
    step: 'theme',
    styleVariantId,
  });
}

/**
 * Build URL for product view step (after design creation)
 */
export function buildProductViewUrl(
  sessionId: string,
  designAssetId: string,
  method?: InputMethod
): string {
  return buildWorkshopUrl({
    sessionId,
    method,
    step: 'product',
    designAssetId,
  });
}

// ============================================================================
// URL PARSING
// ============================================================================

/**
 * Parse workshop URL parameters
 *
 * @param sessionId - Session ID from route params (useParams)
 * @param searchParams - URL search params (useSearchParams or URLSearchParams)
 */
export function parseWorkshopUrl(
  sessionId?: string,
  searchParams?: URLSearchParams
): ParsedWorkshopUrl {
  const stepParam = searchParams?.get('step');
  const explicitStep =
    stepParam === 'theme' || stepParam === 'product' || stepParam === 'style-preview'
      ? (stepParam as UrlStepParam)
      : null;

  // Parse and validate method parameter
  const methodParam = searchParams?.get('method');
  const method =
    methodParam && VALID_INPUT_METHODS.includes(methodParam as InputMethod)
      ? (methodParam as InputMethod)
      : null;

  return {
    sessionId: sessionId ?? null,
    method,
    explicitStep,
    styleVariantId: searchParams?.get('style') ?? null,
    designAssetId: searchParams?.get('design') ?? null,
  };
}

/**
 * Check if a string is a valid input method
 */
export function isValidInputMethod(value: string | null | undefined): value is InputMethod {
  return !!value && VALID_INPUT_METHODS.includes(value as InputMethod);
}

// ============================================================================
// STEP DETERMINATION
// ============================================================================

/**
 * Extended variant type with variant_type field from generated API types
 */
interface ExtendedVariant extends GenerationVariantResponse {
  variant_type?: string;
}

/**
 * Determine the correct workshop step from session state
 *
 * Logic:
 * 1. If pipeline is still processing → 'generating'
 * 2. If pipeline failed → 'generating' (shows error)
 * 3. If has design asset (any variant converted) → 'product-view'
 * 4. If has selected style variant AND has theme variants → 'theme-results'
 * 5. If has style_preview variants (none selected or no themes yet) → 'style-preview'
 * 6. Default → 'generating' (waiting for variants)
 */
export function determineStepFromSession(
  session: GenerationSessionResponse | null | undefined
): WorkshopStep {
  // No session means we're at input
  if (!session) {
    return 'input';
  }

  const { pipeline_state, variants } = session;

  // Still generating or just started
  if (
    pipeline_state === 'started' ||
    pipeline_state === 'generating' ||
    pipeline_state === 'prompt_built'
  ) {
    return 'generating';
  }

  // Pipeline failed - stay on generating to show error
  if (pipeline_state === 'failed') {
    return 'generating';
  }

  // No variants yet - stay on generating
  if (!variants || variants.length === 0) {
    return 'generating';
  }

  // Check if any variant has been converted to a design asset
  const hasDesignAsset = variants.some((v) => v.converted_to_design_asset_id);
  if (hasDesignAsset) {
    return 'product-view';
  }

  // Categorize variants by type
  const styleVariants = variants.filter(
    (v) =>
      !(v as ExtendedVariant).variant_type ||
      (v as ExtendedVariant).variant_type === 'style_preview'
  );
  const themeVariants = variants.filter(
    (v) => (v as ExtendedVariant).variant_type === 'theme_variant'
  );

  // Has theme variants - show theme selection
  if (themeVariants.length > 0) {
    return 'theme-results';
  }

  // Has style variants but no themes yet - show style selection
  if (styleVariants.length > 0) {
    return 'style-preview';
  }

  // Default to generating (shouldn't reach here with completed state)
  return 'generating';
}

/**
 * Determine step considering both session state AND URL parameters
 *
 * URL parameters can override the session-derived step for:
 * - step=theme: Force theme selection view (if session has theme variants)
 * - step=product: Force product view (if session has design asset)
 *
 * This allows direct linking to specific steps while ensuring session state
 * is valid. If session doesn't have expected data yet (race condition),
 * falls back to session state determination which may show loading.
 */
export function determineStepFromUrlAndSession(
  parsedUrl: ParsedWorkshopUrl,
  session: GenerationSessionResponse | null | undefined
): WorkshopStep {
  // If no session ID in URL, we're at input
  if (!parsedUrl.sessionId) {
    return 'input';
  }

  // If URL explicitly specifies product step with design ID
  if (parsedUrl.explicitStep === 'product' && parsedUrl.designAssetId) {
    // Validate session has the design asset before showing product view
    const hasDesignAsset = session?.variants?.some(
      (v) => v.converted_to_design_asset_id === parsedUrl.designAssetId
    );
    if (hasDesignAsset) {
      return 'product-view';
    }
    // Fall through to session state determination if data not ready
  }

  // If URL explicitly specifies style-preview step
  if (parsedUrl.explicitStep === 'style-preview') {
    // Validate session has style variants
    const hasStyleVariants = session?.variants?.some(
      (v) =>
        !(v as ExtendedVariant).variant_type ||
        (v as ExtendedVariant).variant_type === 'style_preview'
    );
    if (hasStyleVariants) {
      return 'style-preview';
    }
  }

  // If URL explicitly specifies theme step with style ID
  if (parsedUrl.explicitStep === 'theme' && parsedUrl.styleVariantId) {
    // Validate session has theme variants before showing theme-results
    // This prevents race conditions where navigation happens before cache is updated
    const hasThemeVariants = session?.variants?.some(
      (v) =>
        (v as ExtendedVariant).variant_type === 'theme_variant' ||
        (v as ExtendedVariant).variant_type === 'selected_style'
    );
    if (hasThemeVariants) {
      return 'theme-results';
    }
    // Fall through to session state determination if data not ready
  }

  // Otherwise, determine from session state
  return determineStepFromSession(session);
}

/**
 * Get the selected style variant ID from session
 */
export function getSelectedStyleVariantId(
  session: GenerationSessionResponse | null | undefined
): string | null {
  if (!session?.variants) return null;

  const selectedStyle = session.variants.find(
    (v) =>
      v.is_selected &&
      (!(v as ExtendedVariant).variant_type ||
        (v as ExtendedVariant).variant_type === 'style_preview')
  );

  return selectedStyle?.id ?? null;
}

/**
 * Get the design asset ID from session (from any converted variant)
 */
export function getDesignAssetIdFromSession(
  session: GenerationSessionResponse | null | undefined
): string | null {
  if (!session?.variants) return null;

  const convertedVariant = session.variants.find((v) => v.converted_to_design_asset_id);
  return convertedVariant?.converted_to_design_asset_id ?? null;
}

// ============================================================================
// NAVIGATION HELPERS
// ============================================================================

/**
 * Check if a step is navigable (completed or current)
 *
 * Navigation is allowed to any step that has been "unlocked" by session progress.
 * This allows users to freely navigate between steps they've already visited.
 */
export function isStepNavigable(
  targetStep: WorkshopStep,
  _currentStep: WorkshopStep,
  session: GenerationSessionResponse | null | undefined
): boolean {
  // NOTE: 'generating' step removed - we go directly to style-preview
  const stepOrder: WorkshopStep[] = ['input', 'style-preview', 'theme-results', 'product-view'];
  const targetIndex = stepOrder.indexOf(targetStep);

  // Can't go back to 'input' once we have a session
  if (targetStep === 'input' && session) {
    return false;
  }

  // Determine the furthest step the session has unlocked
  // This is based on session state, not current step
  const furthestStep = determineStepFromSession(session);
  const furthestIndex = stepOrder.indexOf(furthestStep);

  // Can navigate to any step up to and including the furthest unlocked step
  return targetIndex <= furthestIndex;
}

/**
 * Build the appropriate URL for navigating to a specific step
 * Preserves the input method from the session
 */
export function buildUrlForStep(
  step: WorkshopStep,
  session: GenerationSessionResponse | null | undefined,
  currentMethod?: InputMethod | null
): string | null {
  // Get method from session or use the provided current method
  const method = session?.input_method ?? currentMethod ?? undefined;

  if (!session) {
    if (step === 'input') {
      return method ? buildMethodUrl(method) : '/workshop';
    }
    return null;
  }

  const sessionId = session.id;
  const styleVariantId = getSelectedStyleVariantId(session);
  const designAssetId = getDesignAssetIdFromSession(session);

  switch (step) {
    case 'input':
      return method ? buildMethodUrl(method) : '/workshop';
    case 'generating':
    case 'style-preview':
      return buildStylePreviewUrl(sessionId, method);
    case 'theme-results':
      if (styleVariantId) {
        return buildThemeResultsUrl(sessionId, styleVariantId, method);
      }
      return null;
    case 'product-view':
      if (designAssetId) {
        return buildProductViewUrl(sessionId, designAssetId, method);
      }
      return null;
    default:
      return null;
  }
}
